/**
 * Flow Recorder — Phase 3b, SGRC RPA + Copilot Integration
 *
 * Records the ordered actions / tool-calls fired during a Copilot session into
 * a Flow Definition that can be saved (POST /api/v1/rpa/flows) and later
 * replayed / scheduled / event-triggered (execution = Phase 4 Temporal).
 *
 * The recorder is a passive observer hooked into the Action Bridge: when
 * recording is active, every navigate / fillForm / setField / submitForm /
 * backend-tool call is forwarded here via `recordFromBridge()`.
 *
 * Parameterization turns concrete captured values (e.g. the literal "client X")
 * into `$paramName` placeholders so the flow becomes reusable across inputs.
 */

import type { UiAction } from './action-bridge'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RecordedStep {
  /** Monotonic order index within the recording. */
  order: number
  /** 'ui_action' (Action Bridge op) or 'backend_tool' (Tool Executor call). */
  kind: 'ui_action' | 'backend_tool'
  /** The UI op name (navigate/fillForm/...) or the backend tool/capability code. */
  op?: string
  tool?: string
  /** Captured arguments / values for this step (form values, path, payload...). */
  args: Record<string, unknown>
  /** Optional human label shown in the save dialog. */
  label?: string
  /** Wall-clock timestamp (ms). */
  ts: number
}

export interface RecordedFlow {
  steps: RecordedStep[]
  startedAt: number
  stoppedAt: number
}

export interface ParamBinding {
  /** Dotted path into a step's args, e.g. "values.clientName" or "to". */
  field: string
  /** The $param name to substitute, e.g. "client". */
  paramName: string
}

export interface ParameterizedFlow extends RecordedFlow {
  /** JSON-schema-ish description of the extracted params. */
  params_schema: Record<string, { type: string; example?: unknown; label?: string }>
}

// ── Recorder ──────────────────────────────────────────────────────────────────

export class FlowRecorder {
  private recording = false
  private steps: RecordedStep[] = []
  private startedAt = 0
  private order = 0

  /** Begin a new recording (discards any previous unsaved capture). */
  start(): void {
    this.recording = true
    this.steps = []
    this.order = 0
    this.startedAt = Date.now()
    notifyListeners(true)
  }

  /** True while a recording is active. */
  isRecording(): boolean {
    return this.recording
  }

  /** Number of steps captured so far (for the live counter badge). */
  count(): number {
    return this.steps.length
  }

  /** Snapshot of the steps captured so far (does not stop recording). */
  peek(): RecordedStep[] {
    return [...this.steps]
  }

  /** Record one step. No-op when not recording. */
  record(step: Omit<RecordedStep, 'order' | 'ts'> & { ts?: number }): void {
    if (!this.recording) return
    this.steps.push({
      order: this.order++,
      ts: step.ts ?? Date.now(),
      kind: step.kind,
      op: step.op,
      tool: step.tool,
      args: step.args ?? {},
      label: step.label,
    })
    notifyListeners(true)
  }

  /**
   * Convenience adapter for the Action Bridge: translate a UiAction (and its
   * result) into a RecordedStep. Read-only ops (readContext/highlight/scrollTo)
   * are skipped — only state-changing/navigational steps are recorded.
   */
  recordFromBridge(action: UiAction): void {
    if (!this.recording) return
    const skip = new Set(['readContext', 'highlight', 'scrollTo', 'openTab'])
    if (skip.has(action.op)) return

    let args: Record<string, unknown> = {}
    let label: string = action.op
    switch (action.op) {
      case 'navigate':
        args = { to: action.to, params: action.params ?? {} }
        label = `Naviguer vers ${action.to}`
        break
      case 'fillForm':
        args = { formId: action.formId, values: action.values }
        label = `Remplir le formulaire ${action.formId}`
        break
      case 'setField':
        args = { formId: action.formId, field: action.field, value: action.value }
        label = `Définir ${action.field}`
        break
      case 'submitForm':
        args = { formId: action.formId }
        label = `Soumettre ${action.formId}`
        break
      default:
        args = { ...(action as Record<string, unknown>) }
    }
    this.record({ kind: 'ui_action', op: action.op, args, label })
  }

  /** Record a backend tool / capability call (from the Tool Executor). */
  recordBackendTool(tool: string, args: Record<string, unknown>, label?: string): void {
    if (!this.recording) return
    this.record({ kind: 'backend_tool', tool, args, label: label ?? `Outil ${tool}` })
  }

  /** Stop recording and return the captured ordered flow. */
  stop(): RecordedFlow {
    this.recording = false
    notifyListeners(false)
    return {
      steps: [...this.steps],
      startedAt: this.startedAt,
      stoppedAt: Date.now(),
    }
  }

  /**
   * Replace concrete captured values with `$param` placeholders.
   *
   * For each binding, the value at `step.args[field path]` whose value equals
   * the first occurrence is replaced by the string `"$paramName"` in every
   * step, and an entry is added to `params_schema` (type inferred from the
   * original value, with the original captured as `example`).
   */
  parameterize(flow: RecordedFlow, params: ParamBinding[]): ParameterizedFlow {
    // Deep clone so we never mutate the caller's flow.
    const cloned: RecordedFlow = JSON.parse(JSON.stringify(flow))
    const schema: ParameterizedFlow['params_schema'] = {}

    for (const { field, paramName } of params) {
      let example: unknown
      for (const step of cloned.steps) {
        const original = getByPath(step.args, field)
        if (original !== undefined) {
          if (example === undefined) example = original
          setByPath(step.args, field, `$${paramName}`)
        }
      }
      schema[paramName] = {
        type: inferType(example),
        example,
        label: paramName,
      }
    }

    return { ...cloned, params_schema: schema }
  }
}

// ── Path helpers (support dotted paths like "values.clientName") ──────────────

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[p]
  }
  return cur
}

function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.')
  let cur: Record<string, unknown> = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i]
    if (typeof cur[p] !== 'object' || cur[p] == null) cur[p] = {}
    cur = cur[p] as Record<string, unknown>
  }
  cur[parts[parts.length - 1]] = value
}

function inferType(v: unknown): string {
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  if (Array.isArray(v)) return 'array'
  if (v && typeof v === 'object') return 'object'
  return 'string'
}

// ── Serialization for the backend (steps JSONB) ───────────────────────────────

/**
 * Convert a (possibly parameterized) recorded flow into the `steps` array the
 * backend persists: each step is a plain object with op/tool + args, ordered.
 */
export function toBackendSteps(flow: RecordedFlow): Array<Record<string, unknown>> {
  return flow.steps.map((s) => ({
    order: s.order,
    kind: s.kind,
    op: s.op,
    tool: s.tool,
    args: s.args,
    label: s.label,
  }))
}

// ── Lightweight pub/sub so the sidebar can re-render on recording changes ─────

type Listener = (recording: boolean) => void
const _listeners = new Set<Listener>()

function notifyListeners(recording: boolean): void {
  for (const l of _listeners) {
    try {
      l(recording)
    } catch {
      /* ignore listener errors */
    }
  }
}

export function onRecorderChange(listener: Listener): () => void {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}

// ── Singleton (hooked by the Action Bridge) ───────────────────────────────────

export const flowRecorder = new FlowRecorder()
